/**
 * Date Utilities - Helper functions for date and time formatting operations
 * 
 * Handles various date formatting operations with proper error handling
 * 
 * @author CTS - Ford PM UI Team
 */

import { AppLabels } from '../constant/constant';

/**
 * Private helper function to parse and normalize timestamps
 * @param {string|Date|number} timestamp - The timestamp to parse
 * @returns {Date|null} Parsed Date object or null if invalid
 * @private
 */
const _parseTimestamp = (timestamp) => {
    if (!timestamp) return null;
    
    try {
      // Handle Unix timestamp in seconds (convert to milliseconds)
      let dateValue = timestamp;
      if (typeof timestamp === 'number' && timestamp < 1e12) {
        // If timestamp is less than 1e12, it's likely in seconds, convert to milliseconds
        dateValue = timestamp * 1000;
      }
      
      // Handle DD-MM-YYYY HH:MM:SS format from API responses
      const dateFormatRegex = /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/;
      if (typeof timestamp === 'string' && dateFormatRegex.exec(timestamp)) {
        // Convert DD-MM-YYYY HH:MM:SS to YYYY-MM-DD HH:MM:SS for proper parsing
        const [datePart, timePart] = timestamp.split(' ');
        const [day, month, year] = datePart.split('-');
        dateValue = `${year}-${month}-${day} ${timePart}`;
      }
      
      const date = new Date(dateValue);
      return Number.isNaN(date.getTime()) ? null : date;
    } catch (error) {
      console.error('Error parsing timestamp:', error);
      return null;
    }
};

/**
 * Convert timestamp to readable date format
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted date string in DD-MM-YYYY HH:MM:SS AM/PM format
 */
export const formatDateWithLocalZone = (timestamp) => {
  const date = _parseTimestamp(timestamp);
    if (!date) return AppLabels.notApplicable;
    
    try {
      // Format: DD/MM/YYYY, HH:MM:SS AM/PM with user's local timezone
      return date.toLocaleString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
    } catch (error) {
      console.error('Error formatting heartbeat date:', error);
      return AppLabels.notApplicable;
    }
};

/**
 * Format date to DD-MM-YYYY format only
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted date string in DD-MM-YYYY format
 */
export const formatDateOnly = (timestamp) => {
  const date = _parseTimestamp(timestamp);
    if (!date) return AppLabels.notApplicable;
    
    try {
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return AppLabels.notApplicable;
    }
};

/**
 * Format time to HH:MM:SS format only
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted time string in HH:MM:SS format
 */
export const formatTimeOnly = (timestamp) => {
  const date = _parseTimestamp(timestamp);
    if (!date) return AppLabels.notApplicable;
    
    try {
      return date.toLocaleString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return AppLabels.notApplicable;
    }
};

/**
 * Get relative time string (e.g., "2 hours ago", "3 days ago")
 * @param {string|Date} timestamp - The timestamp to compare
 * @returns {string} Relative time string
 */
export const getRelativeTime = (timestamp) => {
  const date = _parseTimestamp(timestamp);
    if (!date) return AppLabels.notApplicable;
    
    try {
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) return 'Just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
      if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
      if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
      return `${Math.floor(diffInSeconds / 31536000)} years ago`;
    } catch (error) {
      console.error('Error calculating relative time:', error);
      return AppLabels.notApplicable;
    }
};

/**
 * Default export for convenience
 */
const dateUtils = {
  formatDateWithLocalZone,
  formatDateOnly,
  formatTimeOnly,
  getRelativeTime
};

export default dateUtils;
